import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey, useFocusTrap } from '../../../ds/hooks';
import { lockBodyScroll } from '../../../ds/components/Sheet';
import { ChatPanel } from './ChatPanel';
import { TicketSheet } from './TicketSheet';
import { useChat } from './useChat';
import styles from './Questions.module.css';

/** The chat below 768 px: a full-screen sheet (focus trapped, Escape closes) opened by the floating „Fragen". */
export function ChatSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const container = useRef<HTMLDivElement>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);
  useFocusTrap(container, open && ticketId === null, { initialFocus: 'first' });
  useEscapeKey(onClose, open && ticketId === null);

  useEffect(() => (open ? lockBodyScroll() : undefined), [open]);

  if (!open) return null;
  return createPortal(
    <div
      ref={container}
      className={styles.sheet}
      role="dialog"
      aria-modal="true"
      aria-labelledby="portal-chat-sheet-title"
      tabIndex={-1}
    >
      <OpenChat onClose={onClose} onOpenTicket={setTicketId} />
      <TicketSheet ticketId={ticketId} onClose={() => setTicketId(null)} />
    </div>,
    document.body,
  );
}

function OpenChat({ onClose, onOpenTicket }: { onClose: () => void; onOpenTicket: (id: string) => void }) {
  const controller = useChat();
  return (
    <ChatPanel
      controller={controller}
      onClose={onClose}
      onOpenTicket={onOpenTicket}
      className={styles.sheetPanel}
      titleId="portal-chat-sheet-title"
    />
  );
}
